import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, Users, QrCode, BarChart3, Plus, Eye, ArrowRight, CheckCircle2, Clock, Zap, Rocket, Building2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function OrgDashboardPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, onboardingRes] = await Promise.all([
        axios.get(`${API}/api/event/events`, { headers }),
        axios.get(`${API}/api/event/onboarding-status`, { headers }).catch(() => ({ data: null })),
      ]);
      const evtList = Array.isArray(eventsRes.data) ? eventsRes.data : eventsRes.data.events || [];
      setEvents(evtList);
      setOnboarding(onboardingRes.data);

      // Compute stats from events
      const totalEvents = evtList.length;
      const published = evtList.filter(e => e.status === "published").length;
      const totalParticipants = evtList.reduce((s, e) => s + (e.total_registered || e.registered_count || 0), 0);
      const totalCheckins = evtList.reduce((s, e) => s + (e.checked_in_count || 0), 0);
      setStats({ totalEvents, published, totalParticipants, totalCheckins });
    } catch (e) {
      console.error("Fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const kpis = stats ? [
    { label: "Evenements", value: stats.totalEvents, icon: Calendar, color: "text-[#0099CC]", bg: "bg-[#0099CC]/10" },
    { label: "Publies", value: stats.published, icon: Eye, color: "text-[#10B981]", bg: "bg-[#10B981]/10" },
    { label: "Inscrits", value: stats.totalParticipants, icon: Users, color: "text-[#8B5CF6]", bg: "bg-[#8B5CF6]/10" },
    { label: "Check-ins", value: stats.totalCheckins, icon: CheckCircle2, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10" },
  ] : [];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0099CC]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="org-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Mon Espace Evenementiel</h1>
          <p className="text-[#64748b] text-sm mt-1">Gerez vos evenements, participants et check-ins</p>
        </div>
        <div className="flex gap-3">
          <Button
            data-testid="quick-scan-btn"
            onClick={() => navigate("/app/scanner")}
            variant="outline"
            className="border-[#2A3441] text-[#94A3B8] hover:text-white hover:border-[#0099CC]"
          >
            <QrCode className="h-4 w-4 mr-2" />
            Scanner QR
          </Button>
          <Button
            data-testid="create-event-btn"
            onClick={() => navigate("/app/evenements?create=true")}
            className="bg-[#0099CC] hover:bg-[#00AADB] text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvel evenement
          </Button>
        </div>
      </div>

      {/* Onboarding Tunnel */}
      {onboarding && !onboarding.all_done && (
        <div className="bg-gradient-to-r from-[#0B0F19] to-[#0F1629] border border-[#F59E0B]/20 rounded-xl p-5 sm:p-6" data-testid="onboarding-tunnel">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#F59E0B]/10 p-2 rounded-lg">
              <Rocket className="h-5 w-5 text-[#F59E0B]" />
            </div>
            <div>
              <h2 className="text-white font-bold text-base">Bienvenue sur GLOTECH Event Center !</h2>
              <p className="text-[#64748b] text-xs">Completez ces etapes pour etre operationnel</p>
            </div>
            <div className="ml-auto text-right">
              <span className="text-[#F59E0B] font-bold text-lg">{onboarding.progress_percent}%</span>
              <p className="text-[10px] text-[#64748b]">{onboarding.completed}/{onboarding.total} etapes</p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="h-1.5 bg-[#1E293B] rounded-full mb-4 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#F59E0B] to-[#10B981] rounded-full transition-all duration-500"
              style={{ width: `${onboarding.progress_percent}%` }}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {onboarding.steps.map((step, i) => {
              const actions = {
                profile: () => navigate("/app/profil-organisation"),
                create_event: () => navigate("/app/evenements?create=true"),
                publish_event: () => events[0] ? navigate(`/app/evenements/${events[0].id}`) : navigate("/app/evenements"),
                first_registration: () => events.find(e => e.status === "published") ? navigate(`/app/evenements/${events.find(e => e.status === "published").id}`) : null,
              };
              return (
                <button
                  key={step.key}
                  onClick={!step.done ? actions[step.key] : undefined}
                  className={`flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                    step.done
                      ? "bg-[#10B981]/10 border border-[#10B981]/20"
                      : "bg-[#0B0F19] border border-[#1E293B] hover:border-[#F59E0B]/30 cursor-pointer"
                  }`}
                  data-testid={`onboarding-step-${step.key}`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    step.done ? "bg-[#10B981] text-white" : "bg-[#1E293B] text-[#64748b]"
                  }`}>
                    {step.done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-sm ${step.done ? "text-[#10B981] line-through" : "text-white"}`}>
                    {step.label}
                  </span>
                  {!step.done && <ArrowRight className="w-3.5 h-3.5 text-[#64748b] ml-auto" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <div className={`${bg} p-2.5 rounded-lg`}>
                <Icon className={`h-5 w-5 ${color}`} />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-white">{value}</p>
            <p className="text-sm text-[#64748b] mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Events List */}
      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl">
        <div className="flex items-center justify-between p-5 border-b border-[#1E293B]">
          <h2 className="text-lg font-bold text-white">Mes evenements</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/app/evenements")}
            className="text-[#0099CC] hover:text-[#00AADB]"
          >
            Voir tout
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        {events.length === 0 ? (
          <div className="p-10 text-center">
            <Calendar className="h-12 w-12 text-[#2A3441] mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Aucun evenement pour l'instant</h3>
            <p className="text-[#64748b] text-sm mb-4">Creez votre premier evenement pour commencer a recevoir des inscriptions.</p>
            <Button onClick={() => navigate("/app/evenements?create=true")} className="bg-[#0099CC] hover:bg-[#00AADB] text-white">
              <Plus className="h-4 w-4 mr-2" />
              Creer un evenement
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-[#1E293B]">
            {events.slice(0, 5).map(evt => {
              const statusColors = {
                draft: "bg-[#64748b]/20 text-[#94A3B8]",
                published: "bg-[#10B981]/20 text-[#10B981]",
                cancelled: "bg-red-500/20 text-red-400",
                completed: "bg-[#0099CC]/20 text-[#0099CC]",
              };
              const statusLabels = { draft: "Brouillon", published: "Publie", cancelled: "Annule", completed: "Termine" };
              return (
                <div
                  key={evt.id}
                  data-testid="org-event-row"
                  className="flex items-center justify-between p-4 hover:bg-[#0F1420] transition-colors cursor-pointer"
                  onClick={() => navigate(`/app/evenements/${evt.id}`)}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="hidden sm:flex items-center justify-center w-10 h-10 bg-[#0099CC]/10 rounded-lg shrink-0">
                      <Calendar className="h-5 w-5 text-[#0099CC]" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-white truncate">{evt.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-[#64748b] mt-0.5">
                        {evt.start_date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(evt.start_date).toLocaleDateString("fr-FR")}</span>}
                        {evt.location && <span className="truncate">{evt.location}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="hidden sm:flex items-center gap-2 text-sm text-[#94A3B8]">
                      <Users className="h-4 w-4" />
                      <span>{evt.total_registered || evt.registered_count || 0}</span>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[evt.status] || statusColors.draft}`}>
                      {statusLabels[evt.status] || evt.status}
                    </span>
                    <ArrowRight className="h-4 w-4 text-[#2A3441]" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div
          onClick={() => navigate("/app/evenements?create=true")}
          className="bg-[#0B0F19] border border-[#1E293B] hover:border-[#0099CC]/30 rounded-xl p-5 cursor-pointer transition-all group"
        >
          <Plus className="h-8 w-8 text-[#0099CC] mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-semibold text-white">Creer un evenement</h3>
          <p className="text-sm text-[#64748b] mt-1">Nouveau conference, seminaire, ou celebration</p>
        </div>
        <div
          onClick={() => navigate("/app/scanner")}
          className="bg-[#0B0F19] border border-[#1E293B] hover:border-[#10B981]/30 rounded-xl p-5 cursor-pointer transition-all group"
        >
          <QrCode className="h-8 w-8 text-[#10B981] mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-semibold text-white">Scanner QR</h3>
          <p className="text-sm text-[#64748b] mt-1">Controlez les entrees de vos participants</p>
        </div>
        <div
          onClick={() => navigate("/app/evenements")}
          className="bg-[#0B0F19] border border-[#1E293B] hover:border-[#8B5CF6]/30 rounded-xl p-5 cursor-pointer transition-all group"
        >
          <BarChart3 className="h-8 w-8 text-[#8B5CF6] mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-semibold text-white">Mes statistiques</h3>
          <p className="text-sm text-[#64748b] mt-1">Inscriptions, presences et taux en temps reel</p>
        </div>
      </div>
    </div>
  );
}
